import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  AGENT_PERMISSIONS,
  AGENT_BOT_PERMISSIONS,
  ASSIGNMENT_POLICY_PERMISSIONS,
  AUTOMATION_PERMISSIONS,
  CAMPAIGN_PERMISSIONS,
  CONTACT_PERMISSIONS,
  CUSTOM_ATTRIBUTE_PERMISSIONS,
  INBOX_PERMISSIONS,
  INTEGRATION_PERMISSIONS,
  LABEL_PERMISSIONS,
  MACRO_PERMISSIONS,
  OPENAI_PERMISSIONS,
  REPORTS_PERMISSIONS,
  SLA_PERMISSIONS,
  TEAM_PERMISSIONS,
  WEBHOOK_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [AGENT_PERMISSIONS], path: 'settings/agents/list' },
    { permissions: [TEAM_PERMISSIONS], path: 'settings/teams/list' },
    { permissions: [INBOX_PERMISSIONS], path: 'settings/inboxes/list' },
    { permissions: [LABEL_PERMISSIONS], path: 'settings/labels/list' },
    {
      permissions: [CUSTOM_ATTRIBUTE_PERMISSIONS],
      path: 'settings/custom-attributes/list',
    },
    { permissions: [AUTOMATION_PERMISSIONS], path: 'settings/automation/list' },
    { permissions: [AGENT_BOT_PERMISSIONS], path: 'settings/agent-bots' },
    { permissions: [INTEGRATION_PERMISSIONS], path: 'settings/integrations' },
    {
      permissions: [WEBHOOK_PERMISSIONS],
      path: 'settings/integrations/webhook',
    },
    { permissions: [MACRO_PERMISSIONS], path: 'settings/macros' },
    {
      permissions: [ASSIGNMENT_POLICY_PERMISSIONS],
      path: 'settings/assignment-policy/index',
    },
    { permissions: [SLA_PERMISSIONS], path: 'settings/sla/list' },
    { permissions: [CAMPAIGN_PERMISSIONS], path: 'campaigns' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
    { permissions: [OPENAI_PERMISSIONS], path: 'openai' },
    { permissions: ['benefit360_query'], path: 'beneficio-360' },
    { permissions: ['finance_manage'], path: 'logistica/contas-a-receber' },
    { permissions: ['billing_manage'], path: 'settings/billing' },
    { permissions: ['custom_role'], path: 'profile/settings' },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

const validateActiveAccountRoutes = (to, user) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  const routeFeatureFlag = to.meta?.featureFlag;

  if (
    routeFeatureFlag &&
    user.type !== 'SuperAdmin' &&
    !currentAccount?.features?.[routeFeatureFlag]
  ) {
    return accountDashboardURL;
  }

  if (to.meta?.superAdminOrAdmin) {
    const userPermissions = getUserPermissions(user, to.params.accountId);
    const isAdmin = hasPermissions(['administrator'], userPermissions);
    if (user.type === 'SuperAdmin' || isAdmin) {
      return null;
    }
  }

  if (to.meta?.superAdminOnly && user.type !== 'SuperAdmin') {
    return accountDashboardURL;
  }

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);

  if (to.meta?.superAdminOrPermission && user.type === 'SuperAdmin') {
    return null;
  }

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  // If the route is not accessible for the user, return to dashboard screen
  return isAccessible ? null : defaultRedirectPage(to, userPermissions);
};

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive || user.type === 'SuperAdmin') {
    return validateActiveAccountRoutes(to, user);
  }

  // If the current account is not active, only the suspended screen is
  // reachable; administrators can also access billing to restore the account
  const userPermissions = getUserPermissions(user, to.params.accountId);
  const accessibleRoutes = userPermissions.includes('administrator')
    ? ['account_suspended', 'billing_settings_index']
    : ['account_suspended'];

  if (!accessibleRoutes.includes(to.name)) {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'conversation_resolved',
    'conversation_ai',
    'conversation_emails',
    'email_inbox_conversations',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_resolved',
    'conversation_through_ai',
    'conversation_through_emails',
    'conversation_through_email_inbox',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'talkvia_conversations';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversation_through_resolved':
      return 'conversation_resolved';
    case 'conversation_through_ai':
      return 'conversation_ai';
    case 'conversation_through_emails':
      return 'conversation_emails';
    case 'conversation_through_email_inbox':
      return 'email_inbox_conversations';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isUpgradePageBypassRoute = routeName =>
  [
    'billing_settings_index',
    'settings_inbox_list',
    'general_settings_index',
    'agent_list',
  ].includes(routeName);
